/**
 * The firm's clock, in the browser.
 * @file src/web/shared/datetime.ts
 *
 * Every staff screen formats dates through here, in the zone and style the
 * firm chose in Settings — not the viewer's browser zone. See
 * src/shared/datetime.ts for why.
 *
 * ── A module value, not a context ─────────────────────────────────────────
 * The formatters are plain functions, so the twenty-odd places that print a
 * date did not each need a hook. The staff shell calls useFirmDateTime(),
 * which loads the setting once and re-renders the page when it changes —
 * including the moment Settings saves a new one.
 *
 * Until it loads, dates render in US Eastern, US style: the default, and
 * what the great majority of firms keep, so the first paint is right for
 * them and at worst briefly US for anyone else.
 *
 * @llm-rule WHEN: Printing a date or time on a staff screen
 * @llm-rule AVOID: uikit's formatDate or toLocaleString on a staff screen —
 *   both use the viewer's browser zone
 */

import { useEffect, useSyncExternalStore } from 'react';
import {
  DEFAULT_DATETIME,
  dayKeyIn,
  formatIn,
  formatWithIn,
  isDateFormat,
  isTimeZone,
  type DateTimeConfig,
} from '@shared/datetime';
import { fetchFirmSettings } from './hooks/useFirmLanguages';

let config: DateTimeConfig = { ...DEFAULT_DATETIME };
const listeners = new Set<() => void>();

/** Adopt a new clock. Anything unrecognised keeps the current value. */
export function setDateTimeConfig(next: { timeZone?: unknown; format?: unknown }) {
  const timeZone = isTimeZone(next.timeZone) ? next.timeZone : config.timeZone;
  const format = isDateFormat(next.format) ? next.format : config.format;
  if (timeZone === config.timeZone && format === config.format) return;
  config = { timeZone, format };
  listeners.forEach((l) => l());
}

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** Load the firm's clock and re-render when it changes. Called once, by the shell. */
export function useFirmDateTime(enabled = true): DateTimeConfig {
  useEffect(() => {
    /* The consoles have no firm, so no firm clock to load. */
    if (!enabled) return;
    void fetchFirmSettings().then((s) => setDateTimeConfig({ timeZone: s.timezone, format: s.dateFormat }));
  }, [enabled]);
  return useSyncExternalStore(subscribe, () => config);
}

type Input = string | number | Date | null | undefined;
const blank = (v: Input) => v === null || v === undefined || v === '';

/** Sep 11, 2026 — in the firm's zone and style. */
export const formatDate = (v: Input) => (blank(v) ? '' : formatIn(v!, config, 'date'));
/** Sep 11, 2026, 4:30 PM */
export const formatDateTime = (v: Input) => (blank(v) ? '' : formatIn(v!, config, 'datetime'));
/** 4:30 PM */
export const formatTime = (v: Input) => (blank(v) ? '' : formatIn(v!, config, 'time'));
/** Any other shape (a weekday, a month heading), in the firm's zone and convention. */
export const formatWith = (v: Input, opts: Intl.DateTimeFormatOptions) =>
  blank(v) ? '' : formatWithIn(v!, config, opts);
/** "2026-09-11" — the firm's calendar day an instant falls on. For grouping, not display. */
export const dayKey = (v: string | number | Date) => dayKeyIn(v, config.timeZone);

/**
 * A DAY — a deadline, a due date, a date of birth — in the firm's style.
 *
 * Not formatDate: that treats its input as an instant and moves it into the
 * firm's zone, so "2026-03-02" (stored as midnight UTC) would read "Mar 1"
 * for any firm west of Greenwich. A day has no zone. It is read at noon UTC
 * and formatted in UTC, so it comes out as the day it is.
 */
export const formatDay = (day: string | null | undefined) =>
  day ? formatIn(`${day.slice(0, 10)}T12:00:00Z`, { ...config, timeZone: 'UTC' }, 'date') : '';
export const firmTimeZone = () => config.timeZone;
